import {
  BookmarkFill20Px,
  Checkmark20Px,
  CheckmarkCircle20Px,
  GridDesign20Px,
  Search20Px,
  Warning20Px
} from "@locaisolutions/icons";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Collapse
} from "@mui/material";
import { Stack } from "@mui/system";
import { useToast } from "@qubit/autoparts";
import * as Sentry from "@sentry/react";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector } from "~/app/store";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import {
  selectThisWorkstation,
  selectWorkstationAutostoreGridId
} from "~/redux/selectors/workstationsSelectors";
import {
  useDeleteBinFlagMutation,
  usePostFlagBinMutation
} from "~/redux/warehouse/autostoreGrid.hooks";
import { GetBinResponse } from "~/types/api";

import { AlertInfo } from "./AlertInfo";
import { BinReconfigurationModal } from "./BinReconfigurationModal";
import { EscalateModal } from "./EscalateModal";

export function UnflagBinButton({
  binState,
  portId,
  disabled,
  makeNextBinRequest
}: {
  binState: GetBinResponse | undefined;
  portId: number;
  disabled: boolean;
  makeNextBinRequest: (portId: number) => void;
}) {
  const { t } = useTranslation();
  const { successToast, errorToast } = useToast();
  const [deleteBinFlag, { isLoading: isDeletingFlag }] =
    useDeleteBinFlagMutation();
  const [postFlagBin, { isLoading: isPostingFlag }] = usePostFlagBinMutation();
  const trackedPageName = useMixpanelPageName();

  const selectedWorkstation = useAppSelector(selectThisWorkstation);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);

  const { binConfiguration, flags } = binState || {};

  const hasFoodSafetyFlag = flags?.includes("food safety");
  // dont show dirty flag if there is also a food safety flag (they're essentially handled the same way)
  const filteredFlags =
    hasFoodSafetyFlag && flags?.includes("dirty")
      ? flags.filter((flag) => flag !== "dirty")
      : flags;

  const handledFlagTypes = [
    "dirty",
    "food safety",
    "scheduled",
    "maintenance",
    "damaged",
    "misconfigured"
  ];
  const handledFlags = filteredFlags?.filter((flag) =>
    handledFlagTypes.includes(flag)
  );
  const unhandledFlags = filteredFlags?.filter(
    (flag) => !handledFlagTypes.includes(flag)
  );
  const binNumber = binState?.binState.binId;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [cleanBinClicked, setCleanBinClicked] = useState(false);
  const [scheduledClicked, setScheduledClicked] = useState(false);
  const [visualInspectClicked, setVisualInspectClicked] = useState(false);
  const [binReconfigClicked, setBinReconfigClicked] = useState(false);
  const [escalateAlertMessage, setEscalateAlertMessage] = useState<
    null | string
  >(null);
  const [isEscalateModalOpen, setIsEscalateModalOpen] = useState(false);
  const [isBinReconfigurationModalOpen, setIsBinReconfigurationModalOpen] =
    useState(false);

  const resetState = () => {
    setIsModalOpen(false);
    setEscalateAlertMessage(null);
    setIsEscalateModalOpen(false);
    setCleanBinClicked(false);
    setScheduledClicked(false);
    setVisualInspectClicked(false);
    setBinReconfigClicked(false);
  };

  const handleReleaseBin = async (escalate = false) => {
    if (!selectedWorkstation || !autostoreGridId || !binNumber) return;
    const flagsToDelete = flags?.filter((flag) =>
      handledFlagTypes?.includes(flag)
    );
    try {
      for (const flag of flagsToDelete || []) {
        await deleteBinFlag({
          autostoreGridId,
          binNumber,
          reason: flag,
          workstationId: selectedWorkstation.id
        }).unwrap();
      }
      if (escalate) {
        await postFlagBin({
          autostoreGridId,
          binId: binNumber,
          portId: portId,
          reason: "Exterior Maintenance"
        }).unwrap();
        successToast(t("bin escalated"));
      } else {
        successToast(t("flag removed"));
      }

      makeNextBinRequest(portId);
      setIsModalOpen(false);
      resetState();
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  const clickedFlagsCount = [
    cleanBinClicked,
    visualInspectClicked,
    binReconfigClicked,
    scheduledClicked
  ].filter(Boolean).length;
  const hasOnlyUnhandledFlags =
    !handledFlags?.length && !!unhandledFlags?.length;
  const haveAllFlagsBeenHandled =
    clickedFlagsCount === handledFlags?.length || hasOnlyUnhandledFlags;

  return (
    <>
      <Button
        size="large"
        onClick={() => {
          setIsModalOpen(true);
          mixpanelTrack({
            type: "Button Click",
            label: "Unflag Bin",
            trackedPageName,
            eventProperties: {
              binNumber,
              portId
            }
          });
        }}
        startIcon={<BookmarkFill20Px fill="white" />}
        disabled={disabled}
      >
        {`${t("unflag bin")} ${handledFlags?.length ? `(${handledFlags.length})` : ""}`}
      </Button>
      <Dialog open={isModalOpen} maxWidth="xs" onClose={() => resetState()}>
        <DialogTitle>{t("maintenance actions")}</DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Stack direction="column" spacing={2} pt={1}>
            {handledFlags?.map((flag) => {
              if (flag === "dirty" || flag === "food safety") {
                return (
                  <Stack
                    key={flag}
                    direction="row"
                    spacing={5}
                    justifyContent="center"
                  >
                    <Typography alignContent="center" width={"140px"}>
                      {t("dirty bin")}
                    </Typography>
                    <Button
                      variant="subtle"
                      sx={{
                        width: "160px",
                        color: cleanBinClicked ? "success.main" : "black",
                        backgroundColor: cleanBinClicked
                          ? "success.light"
                          : "gray"
                      }}
                      size="large"
                      startIcon={
                        cleanBinClicked ? (
                          <Checkmark20Px fill="green" />
                        ) : (
                          <CheckmarkCircle20Px />
                        )
                      }
                      onClick={() => {
                        setCleanBinClicked(!cleanBinClicked);
                        mixpanelTrack({
                          type: "Button Click",
                          label: "Clean Bin",
                          trackedPageName,
                          eventProperties: {
                            binNumber,
                            portId
                          }
                        });
                      }}
                    >
                      {cleanBinClicked ? t("bin cleaned") : t("clean bin")}
                    </Button>
                  </Stack>
                );
              }
              if (flag === "scheduled" || flag === "maintenance") {
                return (
                  <Stack
                    key={flag}
                    direction="row"
                    spacing={5}
                    justifyContent="center"
                  >
                    <Typography alignContent="center" width={"140px"}>
                      {t("dirty bin (scheduled)")}
                    </Typography>
                    <Button
                      variant="subtle"
                      sx={{
                        width: "160px",
                        color: scheduledClicked ? "success.main" : "black",
                        backgroundColor: scheduledClicked
                          ? "success.light"
                          : "gray"
                      }}
                      size="large"
                      startIcon={
                        scheduledClicked ? (
                          <Checkmark20Px fill="green" />
                        ) : (
                          <CheckmarkCircle20Px />
                        )
                      }
                      onClick={() => {
                        setScheduledClicked(!scheduledClicked);
                        mixpanelTrack({
                          type: "Button Click",
                          label: "Clean Bin (scheduled)",
                          trackedPageName,
                          eventProperties: {
                            binNumber,
                            portId
                          }
                        });
                      }}
                    >
                      {scheduledClicked ? t("bin cleaned") : t("clean bin")}
                    </Button>
                  </Stack>
                );
              }
              if (flag === "damaged") {
                return (
                  <Stack
                    key={flag}
                    direction="row"
                    spacing={5}
                    justifyContent="center"
                  >
                    <Typography alignContent="center" width={"140px"}>
                      {t("damaged bin")}
                    </Typography>
                    <Button
                      variant="subtle"
                      size="large"
                      sx={{
                        width: "160px",
                        color: visualInspectClicked ? "success.main" : "black",
                        backgroundColor: visualInspectClicked
                          ? "success.light"
                          : "gray"
                      }}
                      startIcon={
                        visualInspectClicked ? (
                          <Checkmark20Px fill="green" />
                        ) : (
                          <Search20Px />
                        )
                      }
                      onClick={() => {
                        setVisualInspectClicked(!visualInspectClicked);
                        mixpanelTrack({
                          type: "Button Click",
                          label: "Visual Inspect",
                          trackedPageName,
                          eventProperties: {
                            binNumber,
                            portId
                          }
                        });
                      }}
                    >
                      {t("visual inspect")}
                    </Button>
                  </Stack>
                );
              }
              if (flag === "misconfigured") {
                return (
                  <Stack
                    key={flag}
                    direction="row"
                    spacing={5}
                    justifyContent="center"
                  >
                    <Typography alignContent="center" width={"140px"}>
                      {t("misconfigured bin")}
                    </Typography>
                    <Button
                      variant="subtle"
                      size="large"
                      sx={{
                        width: "160px",
                        color: binReconfigClicked ? "success.main" : "black",
                        backgroundColor: binReconfigClicked
                          ? "success.light"
                          : "gray"
                      }}
                      startIcon={
                        binReconfigClicked ? (
                          <Checkmark20Px fill="green" />
                        ) : (
                          <GridDesign20Px />
                        )
                      }
                      onClick={() => {
                        setIsBinReconfigurationModalOpen(true);
                        mixpanelTrack({
                          type: "Button Click",
                          label: "Reconfigure Bin",
                          trackedPageName,
                          eventProperties: {
                            binNumber,
                            portId
                          }
                        });
                      }}
                    >
                      {t(binReconfigClicked ? "reconfigured" : "reconfigure")}
                    </Button>
                  </Stack>
                );
              } else {
                return <></>;
              }
            })}
            {hasFoodSafetyFlag && (
              <AlertInfo
                message={t(
                  "this bin had a food safety item and must be sanitized"
                )}
              />
            )}
            {!!unhandledFlags?.length && (
              <AlertInfo
                message={`${t("the following flags must be handled elsewhere")}: ${unhandledFlags?.join(", ")}`}
              />
            )}
          </Stack>
        </DialogContent>
        <Collapse in={!!escalateAlertMessage}>
          {!!escalateAlertMessage && (
            <AlertInfo message={escalateAlertMessage} />
          )}
        </Collapse>
        <Collapse in={haveAllFlagsBeenHandled}>
          <DialogActions sx={{ justifyContent: "center", padding: "30px" }}>
            <Button
              size="large"
              variant="serious"
              onClick={() => {
                setIsEscalateModalOpen(true);
                mixpanelTrack({
                  type: "Button Click",
                  label: "Escalate Bin",
                  trackedPageName,
                  eventProperties: {
                    binNumber,
                    portId
                  }
                });
              }}
              startIcon={<Warning20Px fill="white" />}
            >
              {t("escalate")}
            </Button>
            <Button
              size="large"
              disabled={isDeletingFlag || isPostingFlag}
              onClick={async () => {
                await handleReleaseBin();
                mixpanelTrack({
                  type: "Button Click",
                  label: "Release Tote",
                  trackedPageName,
                  eventProperties: {
                    binNumber,
                    portId,
                    from: "Maintenance Actions Modal"
                  }
                });
                if (unhandledFlags?.length) {
                  Sentry.captureMessage(
                    `Unhandled flags detected: ${unhandledFlags?.join(", ")}`,
                    "error"
                  );
                }
              }}
              startIcon={<CheckmarkCircle20Px fill="white" />}
            >
              {t("release bin")}
            </Button>
          </DialogActions>
        </Collapse>
      </Dialog>
      {!!binConfiguration && !!binNumber && (
        <EscalateModal
          open={isEscalateModalOpen}
          onClose={() => {
            setIsEscalateModalOpen(false);
            setEscalateAlertMessage(null);
          }}
          onRelease={() => handleReleaseBin(true)}
          binConfiguration={binConfiguration}
          binNumber={binNumber}
        />
      )}
      {binNumber && flags?.includes("misconfigured") && (
        <BinReconfigurationModal
          open={isBinReconfigurationModalOpen}
          onClose={() => {
            setIsBinReconfigurationModalOpen(false);
          }}
          binNumber={binNumber}
          onConfirm={() => setBinReconfigClicked(true)}
          onCancel={() => setBinReconfigClicked(false)}
        />
      )}
    </>
  );
}
